/**
 * Short URL 내 신청 확인 페이지
 *
 * URL: /{shortCode}/my-application?phone=01012345678
 * short_code를 resolve하여 내 신청 확인 페이지를 렌더링한다.
 * 기존 /apply/{mallId}/{shopNo}/my-application/{campaignId} 와 동일한 UI.
 */
import { MyApplicationContent } from '../apply/$mallId.$shopNo/my-application/$campaignId'
import { PublicCampaignResponse, seedingPublicApi } from '@/lib/seedingPublicApi'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/$shortCode/my-application')({
  component: ShortCodeMyApplicationPage,
  validateSearch: (search) => ({
    phone: (search.phone as string) || '',
  }),
})

function ShortCodeMyApplicationPage() {
  const { shortCode } = Route.useParams()
  const { phone } = Route.useSearch()
  const [campaign, setCampaign] = useState<PublicCampaignResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const resolve = async () => {
      try {
        const data = await seedingPublicApi.resolveShortCode(shortCode)
        setCampaign(data)
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : '캠페인을 찾을 수 없어요.')
      } finally {
        setIsLoading(false)
      }
    }
    resolve()
  }, [shortCode])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-on-surface mb-2">페이지를 찾을 수 없습니다</h1>
          <p className="text-on-surface-variant">{error || '잘못된 링크입니다.'}</p>
        </div>
      </div>
    )
  }

  return (
    <MyApplicationContent
      mallId={campaign.mall_id}
      shopNo={campaign.shop_no ?? 1}
      campaignId={campaign.id}
      phoneFromUrl={phone}
      campaignPath={`/${shortCode}`}
    />
  )
}
